import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TreeView from '@mui/lab/TreeView';
import TreeItem, { treeItemClasses } from '@mui/lab/TreeItem';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {DeleteForever, Description, DescriptionOutlined, Folder, FolderOutlined, Timeline} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { MenuItem, Menu, Checkbox, Button, Tooltip } from "@mui/material";
import {listFiles} from "../API/KB/Files";
import {convertHexToRgba, formatBytes, getVPCService, isMobile, timeAgo} from "../App/utils";
import { getFilePathByKey, isNewFile } from "./CodeEditorUtils";
import { colorText } from "../App/theme";
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from "@mui/material/Divider";
import {useConfirm} from "../Components/ConfirmContext";
import {insecureSecretWarning} from "./SecretsList";
import {kbUserData} from "../utils";


const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
    color: theme.palette.text.secondary,
    [`& .${treeItemClasses.content}`]: {
        marginLeft: 0,
        color: theme.palette.text.secondary,
        borderTopRightRadius: theme.spacing(2),
        borderBottomRightRadius: theme.spacing(2),
        paddingRight: theme.spacing(1),
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: '0.875rem',
        '&.Mui-expanded': {
            fontWeight: theme.typography.fontWeightRegular,
        },
        '&:hover:not(.Mui-selected)': {
            backgroundColor: 'transparent !important',
        },
        '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
            backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
        },
        [`& .${treeItemClasses.label}`]: {
            fontWeight: 'inherit',
            color: 'inherit',
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
        },
    },
    [`& .${treeItemClasses.group}`]: {
        marginLeft: theme.spacing(2),
        [`& .${treeItemClasses.content}`]: {
            paddingLeft: theme.spacing(2),
        },
    },
}));

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
    fontSize: '0.875rem',
    '& .MuiSvgIcon-root': {
        fontSize: '1rem',
        marginRight: theme.spacing(1),
    },
}));

const InfoTypography = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    margin: theme.spacing(2),
}));

function getIconProps(type, inactive, color, size = 18, margin = 0) {
    return {
        width: size,
        src: `/static/icons/${type}${inactive}.svg`,
        alt: type.toUpperCase(),
        style: { marginRight: (8 + margin * 2), color, marginLeft: margin }
    };
}

export function renderItem(item) {
    return item?.folder ? renderFolder(item) : renderFile(item);
}

function renderFile(item, color = null) {
    const rootFolderName = item?.Key?.split('/')?.[2];
    const name = item?.Key?.split('/')?.pop();
    const inactive = color ? '-inactive' : '';
    let iconType = '';
    let size = 16;
    let margin;

    if (name?.endsWith('.json')) {
        return <img {...getIconProps('json', inactive, color, size, margin)} />;
    }

    if (rootFolderName === 'Events' && name?.endsWith('.js')) {
        iconType = 'nodejs';
    } else if (rootFolderName === 'Frontend' && name?.endsWith('.js')) {
        iconType = 'react';
        size = 18;
    }

    if (iconType) {
        return <img {...getIconProps(iconType, inactive, color, size, margin)} />;
    }

    return <Description style={{ marginRight: 8, color, fontSize: '1.25rem' }} />;
}

function renderFolder(item, color = null) {
    const folderName = item?.folder;
    let iconType = '';
    let size = 18;
    let margin;

    if (folderName === 'Frontend') {
        size = 21;
        margin = -1
        iconType = 'react';
    } else if (folderName === 'Events') {
        iconType = 'nodejs-full';
    }

    if (iconType) {
        return <img {...getIconProps(iconType, '', color, size, margin)} />;
    }

    return <Folder style={{ marginRight: 8, fontSize: '1.25rem', color }} />;
}

function StyledTreeItem(props) {
    const theme = useTheme();
    const {
        bgColor,
        color,
        colorForDarkMode,
        bgColorForDarkMode,
        selectedNode,
        isNew,
        filename,
        Size,
        empty,
        LastModified,
        folder,
        onFileDelete,
        onFileCreate,
        item,
        setFilepathVersionsOpen,
        onFileOpen,
        toggleNode, // Function to toggle expansion
        setSelectedNode, // Function to set selected node
        selectionMode,
        onFileSelect,
        selectedFiles,
        toggleSelectionMode, // Function to toggle selection mode
        clearSelection, // Function to clear selection
        ...other
    } = props;
    const [isMenuOpened, setMenuOpened] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const selected = props?.nodeId === props?.selectedNode?.nodeId;
    const styleProps = {
        '--tree-view-color': theme.palette.mode !== 'dark' ? color : colorForDarkMode,
        '--tree-view-bg-color': theme.palette.mode !== 'dark' ? bgColor : bgColorForDarkMode,
    };
    const itemColor = (filename && empty || folder === 'dist') && convertHexToRgba(colorText, 0.3);
    let folderName = folder;
    // if (folder === 'Events') folderName = 'Events';
    // if (folder === 'Frontend') folderName = 'Frontend';
    
    const isSelected = selectedFiles?.some(f => item?.Key && f.path === getFilePathByKey(item.Key));

    const handleLabelClick = (event) => {
        event.stopPropagation();
        setSelectedNode({ nodeId: props.nodeId, item });
        
        // Handle selection mode
        if (selectionMode && !folder && item.Key) {
            onFileSelect(item, event.shiftKey);
            return;
        }
        
        // If single click on mobile, open the file
        if (isMobile) {
            if (folder) {
                // Toggle expansion for folders
                toggleNode(props.nodeId);
            } else if (filename) {
                // Open file on double-click for files
                onFileOpen(item);
            }
        }
    };

    const handleLabelDoubleClick = (event) => {
        event.stopPropagation();
        if (folder) {
            // Toggle expansion for folders
            toggleNode(props.nodeId);
        } else if (filename) {
            const kbId = kbUserData()?.kbId;
            // Open file on double-click for files
            if (item.isUserResource) {
                // For files in the Files folder, open in a new tab
                window.open((getVPCService('file') || 'https://file.openkbs.com/') + `files/${kbId}/${item.Key.split('/').pop()}`, '_blank');
            } else {
                onFileOpen(item);
            }
        }
    };
    
    const handleCheckboxChange = (event) => {
        event.stopPropagation();
        onFileSelect(item, event.shiftKey);
    };

    return (
        <StyledTreeItemRoot
            label={
                <Box
                    onTouchEnd={(event) => {
                        event.stopPropagation();
                    }}
                    onContextMenu={(event) => {
                        event.preventDefault();
                        setAnchorEl(event.currentTarget);
                        setMenuOpened(!isMenuOpened);
                    }}
                    onClick={handleLabelClick}
                    onDoubleClick={handleLabelDoubleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: folder ? '1px' : '1px',
                        marginBottom: folder ? '1px' : '1px',
                        p: 0.1,
                        pb: 0.2,
                        pr: 0,
                        userSelect: 'none', // Prevent text selection on double click
                        cursor: 'pointer',
                    }}
                >
                    {selectionMode && !folder && (
                        <Checkbox
                            checked={isSelected}
                            onClick={handleCheckboxChange}
                            size="small"
                            sx={{ 
                                padding: '0px', 
                                marginRight: '4px',
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                    color: theme.palette.primary.main,
                                },
                            }}
                        />
                    )}
                    <Box
                        component={filename ? () => renderFile(item, itemColor) : () => renderFolder(item, itemColor)}
                        color="inherit"
                        sx={{ mr: 1 }}
                    />
                    <Typography component="div" variant="body2" sx={{
                        fontWeight: 'inherit',
                        flexGrow: 1,
                        color: itemColor
                    }}>
                        {filename || folderName}
                    </Typography>
                    <ClickAwayListener
                        onClickAway={(event) => {
                            event.stopPropagation();
                            setMenuOpened(false);
                            setAnchorEl(null);
                        }}
                    >
                        <Menu
                            open={isMenuOpened}
                            style={{ zIndex: 9999 }}
                            anchorEl={anchorEl}
                            onClose={(event) => {
                                event.stopPropagation();
                                setMenuOpened(false);
                                setAnchorEl(null);
                            }}
                        >
                            {(empty && !item?.folder) ? (
                                <CustomMenuItem
                                    onClick={(e) => {
                                        onFileOpen(item);
                                        setMenuOpened(false);
                                        e.stopPropagation();
                                    }}
                                >
                                    Click to create
                                </CustomMenuItem>
                            ) : (
                                <div>
                                    {item?.folder && (
                                        <CustomMenuItem
                                            onClick={(e) => {
                                                onFileCreate(item);
                                                setMenuOpened(false);
                                                e.stopPropagation();
                                            }}
                                        >
                                            <DescriptionOutlined />
                                            New File
                                        </CustomMenuItem>
                                    )}
                                    {item?.folder && (
                                        <CustomMenuItem
                                            onClick={(e) => {
                                                onFileCreate({...item, isFolder: true});
                                                setMenuOpened(false);
                                                e.stopPropagation();
                                            }}
                                        >
                                            <FolderOutlined />
                                            New Folder
                                        </CustomMenuItem>
                                    )}
                                    {/*{item?.Key && (*/}
                                    {/*    <CustomMenuItem*/}
                                    {/*        onClick={() => {*/}
                                    {/*            setMenuOpened(false);*/}
                                    {/*            setFilepathVersionsOpen(getFilePathByKey(item.Key));*/}
                                    {/*        }}*/}
                                    {/*    >*/}
                                    {/*        <Timeline />*/}
                                    {/*        Show History*/}
                                    {/*    </CustomMenuItem>*/}
                                    {/*)}*/}
                                    <CustomMenuItem
                                        onClick={(e) => {
                                            onFileDelete();
                                            setMenuOpened(false);
                                            e.stopPropagation();
                                        }}
                                    >
                                        <DeleteForever />
                                        Delete{item?.folder && ` files in "${item.folder}"`}
                                    </CustomMenuItem>
                                    {!item?.folder && (
                                        <CustomMenuItem
                                            onClick={(e) => {
                                                props.toggleSelectionMode(!selectionMode);
                                                if (!selectionMode) {
                                                    // If turning on selection mode, add current item to selection
                                                    onFileSelect(item);
                                                } else {
                                                    // If turning off, clear selection
                                                    props.clearSelection();
                                                }
                                                setMenuOpened(false);
                                                e.stopPropagation();
                                            }}
                                        >
                                            <Checkbox size="small" sx={{ padding: 0 }} />
                                            {selectionMode ? "Exit Selection Mode" : "Select Multiple Files"}
                                        </CustomMenuItem>
                                    )}
                                    <Divider><InfoTypography style={{margin: 0, padding: 0}} >Info</InfoTypography></ Divider>
                                    <InfoTypography style={{marginTop: 2, marginBottom: 2}}>{item?.folder ? 'Folder' : 'File'}: {(item?.Key || item?.folder)?.split('/')?.splice(-1)}</InfoTypography>
                                    {!!LastModified && <InfoTypography style={{marginTop: 2, marginBottom: 2}}>Size: {formatBytes(Size)}</InfoTypography>}
                                    {!!LastModified && <InfoTypography style={{marginTop: 2, marginBottom: 2}}>Modified: {timeAgo(LastModified)}</InfoTypography>}
                                </div>
                            )}
                        </Menu>
                    </ClickAwayListener>
                </Box>
            }
            style={styleProps}
            selected={selected} // Ensure TreeItem highlights as selected
            {...other}
        />
    );
}

export function FilesEditor({ isFullscreen, onFileOpen, onFileCreate, onFileDelete, setFilesData, filesData, setFilepathVersionsOpen }) {
    const [selectedNode, setSelectedNode] = useState(null);
    const [fileItemCreateLastSeconds, setFileItemCreateLastSeconds] = useState(null);
    const [expanded, setExpanded] = useState(['1','2','3','4']);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const [lastSelectedItem, setLastSelectedItem] = useState(null);

    const showConfirm = useConfirm();

    useEffect(() => {
        const itemCreateLastSeconds = filesData?.flatMap(item => item?.children || [])
            .sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified))
            .find(child => isNewFile(child));
        setFileItemCreateLastSeconds(itemCreateLastSeconds || null)
    }, [filesData]);

    useEffect(() => {
        // Create a single function to handle all API calls
        const loadAllFiles = async () => {
            try {
                // Use Promise.all to fetch all data in parallel
                const [frontendFiles, functionsFiles, userFiles] = await Promise.all([
                    listFiles('frontend'),
                    listFiles('functions'),
                    listFiles('files')
                ]);

                // Prepare the user files structure if needed
                const filesStructure = userFiles && userFiles.length > 0 ? {
                    folder: 'Files',
                    children: userFiles.map(file => ({
                        ...file,
                        filename: file.filename,
                        Size: file.Size,
                        Key: file.Key,
                        LastModified: file.LastModified,
                        isUserResource: true
                    }))
                } : null;

                // Set all data at once with a single state update
                setFilesData([
                    ...frontendFiles,
                    ...functionsFiles,
                    ...(filesStructure ? [filesStructure] : [])
                ]);
            } catch (error) {
                console.error('Error loading files:', error);
                // Handle error appropriately
            }
        };

        loadAllFiles();
    }, [setFilesData]);
    
    // Function to flatten the file structure into a list for range selection
    const getAllFiles = () => {
        const files = [];
        
        const traverseTree = (items) => {
            items.forEach(item => {
                // Add files but not folders
                if (item.Key && !item.folder) {
                    files.push(item);
                }
                
                // Recursively traverse children
                if (item.children && item.children.length > 0) {
                    traverseTree(item.children);
                }
            });
        };
        
        traverseTree(filesData);
        return files;
    };
    
    const handleFileSelect = (item, isShiftKey = false) => {
        console.log('handleFileSelect called', { item, isShiftKey, selectionMode });
        if (!item.Key) return; // Only select files, not folders
        
        if (isShiftKey && lastSelectedItem && lastSelectedItem.Key) {
            // Get all files in a flat list
            const allFiles = getAllFiles();
            
            // Find indices of current and last selected items
            const currentIndex = allFiles.findIndex(f => f.Key === item.Key);
            const lastIndex = allFiles.findIndex(f => f.Key === lastSelectedItem.Key);
            
            if (currentIndex !== -1 && lastIndex !== -1) {
                // Determine range start and end
                const startIndex = Math.min(currentIndex, lastIndex);
                const endIndex = Math.max(currentIndex, lastIndex);
                
                // Get files in the range
                const filesToSelect = allFiles.slice(startIndex, endIndex + 1);
                
                // Add all files in range to selection
                const newSelection = [...selectedFiles];
                
                filesToSelect.forEach(fileItem => {
                    const fileKey = getFilePathByKey(fileItem.Key);
                    if (!newSelection.some(f => f.path === fileKey)) {
                        newSelection.push({ path: fileKey, item: fileItem });
                    }
                });
                
                setSelectedFiles(newSelection);
            }
        } else {
            // Regular single selection toggle
            setSelectedFiles(prev => {
                const fileKey = getFilePathByKey(item.Key);
                if (prev.some(f => f.path === fileKey)) {
                    return prev.filter(f => f.path !== fileKey);
                } else {
                    return [...prev, { path: fileKey, item }];
                }
            });
            
            // Update the last selected item
            setLastSelectedItem(item);
        }
    };
    
    const deleteSelectedFiles = async () => {
        if (selectedFiles.length === 0) return;
        
        // Create a more readable file list for confirmation
        const fileList = selectedFiles.map(f => f.path).join('\n- ');
        const confirmed = await showConfirm({
            title: 'Delete Multiple Files',
            content: `Are you sure you want to delete these ${selectedFiles.length} files?\n\n- ${fileList}`,
        });
        
        if (!confirmed) return;
        
        // Create a copy of the files to delete
        const filesToDelete = [...selectedFiles];
        
        // Show visual feedback during deletion
        const button = document.querySelector('[data-delete-button]');
        const originalText = button ? button.textContent : 'Delete Selected';
        
        try {
            if (button) {
                button.disabled = true;
                button.textContent = `Deleting (0/${filesToDelete.length})...`;
            }
            
            // Delete each file
            for (let i = 0; i < filesToDelete.length; i++) {
                const file = filesToDelete[i];
                // Ensure onFileDelete is treated as a promise
                await Promise.resolve(onFileDelete(file.path));
                
                // Small delay to ensure UI updates and not overwhelm the server
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Update progress
                if (button) {
                    button.textContent = `Deleting (${i + 1}/${filesToDelete.length})...`;
                }
            }
            
            // Clear selection
            setSelectedFiles([]);
            setSelectionMode(false);
        } catch (error) {
            console.error('Error during deletion:', error);
            if (button) {
                button.textContent = 'Error: Delete Failed';
                setTimeout(() => {
                    button.textContent = originalText;
                    button.disabled = false;
                }, 2000);
            }
        } finally {
            if (button) {
                button.disabled = false;
                button.textContent = originalText;
            }
        }
    };

    const toggleNode = (nodeId) => {
        setExpanded((prevExpanded) => {
            if (prevExpanded.includes(nodeId)) {
                return prevExpanded.filter(id => id !== nodeId);
            } else {
                return [...prevExpanded, nodeId];
            }
        });
    };

    const handleNodeToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    const renderTree = (items, startNodeId = 0) => {
        let nodeId = startNodeId;
        items.sort((a, b) => {
            // Special case: Files folder should always be at the bottom
            if (a.folder === 'Files') return 1;
            if (b.folder === 'Files') return -1;

            const isAFolder = a.hasOwnProperty('folder');
            const isBFolder = b.hasOwnProperty('folder');
            if (isAFolder && !isBFolder) return -1;
            if (!isAFolder && isBFolder) return 1;
            const nameA = (a?.folder || a?.filename)?.toUpperCase();
            const nameB = (b?.folder || b?.filename)?.toUpperCase();
            const baseNameA = nameA.split('.')[0];
            const baseNameB = nameB.split('.')[0];
            const extensionA = nameA.split('.').pop();
            const extensionB = nameB.split('.').pop();
            if (baseNameA === baseNameB) {
                if (extensionA === 'JS' && extensionB === 'JSON') return -1;
                if (extensionA === 'JSON' && extensionB === 'JS') return 1;
            }
            if (nameA > nameB) return -1;
            if (nameA < nameB) return 1;
            return 0;
        });

        const jsFilesStatus = new Map(
            items.filter((item) => item.filename && item.filename.endsWith('.js')).map((item) => [item.filename.replace('.js', ''), item.empty])
        );

        return items.map((item) => {
            nodeId++;
            const currentId = nodeId.toString();
            if (item.empty && item.filename && item.filename.endsWith('.json')) {
                const baseName = item.filename.replace('.json', '');
                if (jsFilesStatus.has(baseName) && jsFilesStatus.get(baseName)) return null;
            }
            const isNew = fileItemCreateLastSeconds?.Key && fileItemCreateLastSeconds?.Key === item?.Key && isNewFile(fileItemCreateLastSeconds);
            return (
                <Box key={currentId} sx={{ textAlign: 'left' }}>
                    <StyledTreeItem
                        className={isNew ? 'new-file-created' : ''}
                        onFileDelete={async () => {
                            const itemToDelete = item?.folder ? (`all files in ${item.folder}`) : getFilePathByKey(item.Key);

                            const confirmed = await showConfirm({
                                title: 'Delete Confirmation',
                                content: `Are you sure you want to delete "${itemToDelete}"?`,
                            });

                            if (!confirmed) return;
                            (item?.children ?? [item])
                                .forEach(({ filename, Key }) => (filename || filename === '') && Key && onFileDelete(getFilePathByKey(Key)))
                        }}
                        onFileCreate={onFileCreate}
                        item={item}
                        setFilepathVersionsOpen={setFilepathVersionsOpen}
                        empty={item?.empty}
                        Size={item?.Size}
                        LastModified={item?.LastModified}
                        filename={item?.filename}
                        folder={item?.folder}
                        selectedNode={selectedNode}
                        isNew={isNew}
                        nodeId={currentId}
                        color={item.color}
                        bgColor={item.bgColor}
                        colorForDarkMode={item.colorForDarkMode}
                        bgColorForDarkMode={item.bgColorForDarkMode}
                        onFileOpen={onFileOpen}
                        toggleNode={toggleNode} // Pass toggleNode to StyledTreeItem
                        setSelectedNode={setSelectedNode} // Pass setSelectedNode to StyledTreeItem
                        selectionMode={selectionMode}
                        onFileSelect={handleFileSelect}
                        selectedFiles={selectedFiles}
                        toggleSelectionMode={setSelectionMode}
                        clearSelection={() => setSelectedFiles([])}
                    >
                        {item.children && item.children?.find(o => o.filename || o.folder)
                            // filter out empty file in folder
                            && renderTree(item.children?.filter(o => o.filename !== ''), nodeId * 1000)}
                    </StyledTreeItem>
                </Box>
            );
        });
    };

    return (
        <>
            {selectionMode && (
                <Box sx={{ 
                    padding: '8px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    backgroundColor: theme => theme.palette.background.paper,
                    borderBottom: '1px solid',
                    borderColor: theme => theme.palette.divider,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="Tip: Hold Shift and click to select a range of files">
                            <Typography variant="body2" sx={{ mr: 2 }}>
                                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                            </Typography>
                        </Tooltip>
                        
                        <Button 
                            color="error" 
                            size="small" 
                            variant="contained" 
                            onClick={deleteSelectedFiles}
                            startIcon={<DeleteForever />}
                            disabled={selectedFiles.length === 0}
                            data-delete-button
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            )}
            <TreeView
                aria-label="functions manager"
                expanded={expanded}
                onNodeToggle={handleNodeToggle}
                defaultCollapseIcon={<ArrowDropDownIcon />}
                defaultExpandIcon={<ArrowRightIcon />}
                defaultEndIcon={<div style={{ width: 24 }} />}
                selected={selectedNode ? selectedNode.nodeId : null} // Ensure only one node is selected
                sx={{ height: selectionMode ? 'calc(100% - 53px)' : '100%', flexGrow: 1, marginLeft: isFullscreen ? '0px' : '-4px', overflowY: 'auto' }}
            >
                {renderTree(filesData)}
            </TreeView>
        </>
    );
}